import { initials, money, fmtDate, fmtDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/shared";

// column render helpers
const t = (v) => v ?? "—";
const cell = { status: (v) => <StatusBadge value={v} /> };

export const MODULES = {
  clients: {
    endpoint: "/clients", title: "Clients", subtitle: "Client accounts and relationships",
    searchable: true, perm: "client:view",
    columns: [
      { key: "company_name", label: "Company", render: (r) => (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg grid place-items-center text-white text-[10px] font-bold" style={{ background: r.logo_color || "#255058" }}>{r.logo_symbol || initials(r.company_name)}</div>
          <div><div className="font-medium">{r.company_name}</div><div className="text-xs text-muted-foreground">{r.industry}</div></div>
        </div>) },
      { key: "region", label: "Region" },
      { key: "tier", label: "Tier", render: (r) => <StatusBadge value={r.tier} /> },
      { key: "health", label: "Health", render: (r) => <StatusBadge value={r.health} /> },
      { key: "status", label: "Status", render: (r) => cell.status(r.status) },
      { key: "owner_name", label: "Owner" },
    ],
  },
  contacts: {
    endpoint: "/contacts", title: "Contacts", subtitle: "Client contacts across accounts", perm: "contact:view",
    columns: [
      { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
      { key: "title", label: "Title" },
      { key: "client_name", label: "Client" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "is_primary", label: "Primary", render: (r) => r.is_primary ? <StatusBadge value="active" /> : "—" },
    ],
  },
  sales: {
    endpoint: "/leads", title: "Sales CRM", subtitle: "Business development pipeline", perm: "sales:view",
    columns: [
      { key: "company", label: "Company", render: (r) => <span className="font-medium">{r.company}</span> },
      { key: "stage", label: "Stage", render: (r) => cell.status(r.stage) },
      { key: "value", label: "Value", render: (r) => money(r.value, r.currency) },
      { key: "probability", label: "Probability", render: (r) => `${t(r.probability)}%` },
      { key: "source", label: "Source" },
      { key: "owner_name", label: "Owner" },
    ],
  },
  jobs: {
    endpoint: "/jobs", title: "Jobs", subtitle: "Open requisitions and roles", searchable: true, perm: "job:view",
    columns: [
      { key: "title", label: "Job", render: (r) => <div><div className="font-medium">{r.title}</div><div className="text-xs text-muted-foreground">{r.requisition_id} · {r.client_name}</div></div> },
      { key: "location", label: "Location" },
      { key: "work_model", label: "Model" },
      { key: "priority", label: "Priority", render: (r) => <StatusBadge value={r.priority} /> },
      { key: "status", label: "Status", render: (r) => cell.status(r.status) },
      { key: "days_open", label: "Days Open" },
    ],
  },
  submissions: {
    endpoint: "/submissions", title: "Submissions", subtitle: "Candidate submissions to clients", perm: "submission:view",
    columns: [
      { key: "candidate_name", label: "Candidate", render: (r) => <span className="font-medium">{r.candidate_name}</span> },
      { key: "job_title", label: "Job" },
      { key: "client_name", label: "Client" },
      { key: "recruiter_name", label: "Recruiter" },
      { key: "stage", label: "Stage", render: (r) => cell.status(r.stage) },
      { key: "submitted_at", label: "Submitted", render: (r) => fmtDate(r.submitted_at) },
    ],
  },
  interviews: {
    endpoint: "/interviews", title: "Interviews", subtitle: "Scheduled and completed interviews", perm: "interview:view",
    columns: [
      { key: "candidate_name", label: "Candidate", render: (r) => <span className="font-medium">{r.candidate_name}</span> },
      { key: "client_name", label: "Client" },
      { key: "interview_type", label: "Type" },
      { key: "scheduled_at", label: "Scheduled", render: (r) => fmtDateTime(r.scheduled_at) },
      { key: "status", label: "Status", render: (r) => cell.status(r.status) },
      { key: "rating", label: "Rating", render: (r) => r.rating ? `${r.rating}/5` : "—" },
    ],
  },
  offers: {
    endpoint: "/offers", title: "Offers", subtitle: "Offer management and status", perm: "offer:view",
    columns: [
      { key: "candidate_name", label: "Candidate", render: (r) => <span className="font-medium">{r.candidate_name}</span> },
      { key: "client_name", label: "Client" },
      { key: "base_comp", label: "Base", render: (r) => money(r.base_comp, r.currency) },
      { key: "status", label: "Status", render: (r) => cell.status(r.status) },
      { key: "offer_date", label: "Offered", render: (r) => fmtDate(r.offer_date) },
    ],
  },
  placements: {
    endpoint: "/placements", title: "Placements", subtitle: "Confirmed placements and warranty", perm: "placement:view",
    columns: [
      { key: "candidate_name", label: "Candidate", render: (r) => <span className="font-medium">{r.candidate_name}</span> },
      { key: "client_name", label: "Client" },
      { key: "placement_fee", label: "Fee", render: (r) => money(r.placement_fee, r.currency) },
      { key: "start_date", label: "Start", render: (r) => fmtDate(r.start_date) },
      { key: "guarantee_end", label: "Guarantee Ends", render: (r) => fmtDate(r.guarantee_end) },
      { key: "warranty_risk", label: "Risk", render: (r) => <StatusBadge value={r.warranty_risk} /> },
    ],
  },
  revenue: {
    endpoint: "/revenue/invoices", title: "Revenue & Invoices", subtitle: "Invoicing and collections", perm: "revenue:view",
    columns: [
      { key: "invoice_number", label: "Invoice", render: (r) => <span className="font-mono text-xs font-medium">{r.invoice_number}</span> },
      { key: "client_name", label: "Client" },
      { key: "total", label: "Total", render: (r) => money(r.total, r.currency) },
      { key: "outstanding_amount", label: "Outstanding", render: (r) => money(r.outstanding_amount, r.currency) },
      { key: "status", label: "Status", render: (r) => cell.status(r.status) },
      { key: "due_date", label: "Due", render: (r) => fmtDate(r.due_date) },
    ],
  },
  hrms: {
    endpoint: "/employees", title: "Employees & HRMS", subtitle: "Internal team — separate from candidates", perm: "hrms:view",
    columns: [
      { key: "name", label: "Employee", render: (r) => <div className="flex items-center gap-3"><div className="h-8 w-8 rounded-full bg-[#255058] text-white text-xs grid place-items-center">{initials(r.name)}</div><div><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.employee_id}</div></div></div> },
      { key: "designation", label: "Designation" },
      { key: "department", label: "Department" },
      { key: "region", label: "Region" },
      { key: "employment_status", label: "Status", render: (r) => cell.status(r.employment_status) },
      { key: "compensation", label: "Compensation", render: (r) => "compensation" in r ? money(r.compensation) : "Restricted" },
    ],
  },
  tasks: {
    endpoint: "/tasks", title: "Tasks", subtitle: "Your tasks and follow-ups", perm: null,
    columns: [
      { key: "title", label: "Task", render: (r) => <span className="font-medium">{r.title}</span> },
      { key: "type", label: "Type", render: (r) => <StatusBadge value={r.type} /> },
      { key: "priority", label: "Priority", render: (r) => <StatusBadge value={r.priority} /> },
      { key: "status", label: "Status", render: (r) => cell.status(r.status) },
      { key: "due_at", label: "Due", render: (r) => fmtDate(r.due_at) },
      { key: "assignee_name", label: "Assignee" },
    ],
  },
};
